import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TenantApiError } from "@qr-menu/api-tenant";
import { Card, CardContent, CardHeader, CardTitle } from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import { readTenantToken } from "../lib/session";

const NO_TOKEN_MESSAGE =
  "Bu e-posta doğrulama bağlantısı bu oturumda kullanılamıyor.";
const VERIFYING_MESSAGE = "E-posta adresiniz doğrulanıyor…";
const SUCCESS_MESSAGE = "E-posta adresiniz doğrulandı.";
const REJECTED_MESSAGE =
  "Bu e-posta doğrulama bağlantısı geçersiz veya süresi dolmuş.";
const OPERATIONAL_ERROR_MESSAGE =
  "Şu anda doğrulama yapılamıyor. Lütfen daha sonra tekrar deneyin.";

// A loose sanity check only — length and URL-safe base64 charset. The
// backend (AuthService.VerifyEmail / parseCanonicalVerificationToken)
// remains the sole authority on the exact canonical format (43 chars, strict
// Raw URL base64, 32 decoded bytes, canonical re-encoding); this is not a
// second copy of that policy, just enough to reject an obviously-wrong
// fragment (empty, or carrying something that isn't a token at all) before
// ever attempting the request. Mirrors ResetPassword.tsx's identical check.
const TOKEN_CHARSET_PATTERN = /^[A-Za-z0-9_-]+$/;
const MIN_PLAUSIBLE_TOKEN_LENGTH = 20;

function isPlausibleToken(value: string): boolean {
  return (
    value.length >= MIN_PLAUSIBLE_TOKEN_LENGTH &&
    TOKEN_CHARSET_PATTERN.test(value)
  );
}

// Reads only the URL fragment (window.location.hash), never the query
// string: the verification link is <origin>/verify-email#token=<token>,
// mirroring ResetPassword.tsx exactly. Manual parsing on purpose — no
// URLSearchParams/useSearchParams on location.search, since that would read
// the wrong part of the URL.
function extractTokenFromFragment(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;

  for (const pair of raw.split("&")) {
    const [key, value] = pair.split("=");
    if (key === "token" && value) {
      try {
        return decodeURIComponent(value);
      } catch {
        return null;
      }
    }
  }
  return null;
}

type VerifyOutcome = "verifying" | "success" | "rejected" | "operational-error";

function PostVerifyCTA() {
  return readTenantToken() ? (
    <Link to="/app/account">Hesabıma dön</Link>
  ) : (
    <Link to="/login">Giriş yap</Link>
  );
}

export function VerifyEmailRoute() {
  // Read once, synchronously, during the first render — directly from
  // window.location.hash. This is the ONLY place the token is ever read
  // from the URL. From here on it lives only in this component's state:
  // never in router/navigation state, a global store, localStorage,
  // sessionStorage, a cookie, the tenant session module, or a log line.
  const [token] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const extracted = extractTokenFromFragment(window.location.hash);
    return extracted && isPlausibleToken(extracted) ? extracted : null;
  });

  // Strips the fragment from the visible address bar before the browser
  // paints, so the raw token spends as little time as possible visible in
  // the URL bar, browser history, or anywhere a screenshot/share could catch
  // it. Mirrors ResetPassword.tsx's identical guard/history handling,
  // including preserving react-router's own history.state bookkeeping.
  const strippedRef = useRef(false);
  useLayoutEffect(() => {
    if (strippedRef.current) return;
    strippedRef.current = true;
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      const url = window.location.pathname + window.location.search;
      window.history.replaceState(window.history.state, "", url);
    }
  }, []);

  const [outcome, setOutcome] = useState<VerifyOutcome>("verifying");
  // Disables the retry button for the duration of a retry request, matching
  // the submitting-flag convention used by ResetPassword.tsx/Account.tsx's
  // forms — belt-and-suspenders alongside the button only existing in the
  // DOM while outcome === "operational-error", so one click can only ever
  // produce one in-flight request.
  const [retrying, setRetrying] = useState(false);

  // Guards the auto-verify POST against React 18 StrictMode's development
  // double-invocation of effects: this ref ensures at most one request is
  // ever sent for this mount, regardless of how many times the effect body
  // runs. A user-triggered retry (see below) is a separate, explicit call
  // and is allowed to fire again.
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    if (requestedRef.current) return;
    requestedRef.current = true;

    let cancelled = false;

    tenantApi
      .verifyEmail({ token })
      .then(() => {
        if (!cancelled) setOutcome("success");
      })
      .catch((err) => {
        if (cancelled) return;
        // Status-only mapping, never the backend's error_message text: a 422
        // means the token itself was rejected (unknown/expired/used/inactive
        // account — the backend deliberately never distinguishes which).
        // Anything else (429, 5xx, or a plain network failure) is an
        // operational problem, not an invalid-token verdict, and must not be
        // presented as one.
        if (err instanceof TenantApiError && err.status === 422) {
          setOutcome("rejected");
          return;
        }
        setOutcome("operational-error");
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function retry() {
    if (!token || outcome !== "operational-error" || retrying) return;
    setRetrying(true);
    tenantApi
      .verifyEmail({ token })
      .then(() => setOutcome("success"))
      .catch((err) => {
        if (err instanceof TenantApiError && err.status === 422) {
          setOutcome("rejected");
          return;
        }
        setOutcome("operational-error");
      })
      .finally(() => setRetrying(false));
  }

  if (!token) {
    return (
      <div className="auth-shell">
        <Card className="auth-card">
          <CardHeader>
            <CardTitle>E-posta doğrulama</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="state error">{NO_TOKEN_MESSAGE}</div>
            <p className="hint">
              <PostVerifyCTA />
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>E-posta doğrulama</CardTitle>
        </CardHeader>
        <CardContent>
          {outcome === "verifying" && (
            <div className="state">{VERIFYING_MESSAGE}</div>
          )}

          {outcome === "success" && (
            <>
              <div className="state ok">{SUCCESS_MESSAGE}</div>
              <p className="hint">
                <PostVerifyCTA />
              </p>
            </>
          )}

          {outcome === "rejected" && (
            <>
              <div className="state error">{REJECTED_MESSAGE}</div>
              <p className="hint">
                <PostVerifyCTA />
              </p>
            </>
          )}

          {outcome === "operational-error" && (
            <>
              <div className="state error">{OPERATIONAL_ERROR_MESSAGE}</div>
              <div className="login-actions">
                <button
                  type="button"
                  className="primary"
                  onClick={retry}
                  disabled={retrying}
                >
                  {retrying ? "Deneniyor…" : "Tekrar dene"}
                </button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
